import { useEffect, useRef, useState } from 'react';

/**
 * Fires once when the element scrolls into view, then disconnects. The landing
 * page has a dozen animated sections; leaving observers attached for all of
 * them costs more than the animation is worth.
 */
export function useInViewOnce({ rootMargin = '0px 0px -12% 0px', failOpenAfter = 2000 } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin, threshold: 0.05 },
    );
    io.observe(el);

    // Fail open. A reveal that never fires leaves its content invisible, which
    // is a far worse outcome than one that appears without animating.
    const bail = setTimeout(() => {
      setInView(true);
      io.disconnect();
    }, failOpenAfter);

    return () => {
      clearTimeout(bail);
      io.disconnect();
    };
  }, [inView, rootMargin, failOpenAfter]);

  return [ref, inView];
}
